import React, { useEffect, useState } from 'react';
import { fetchClientSummary } from '../../services/vectorDashboardApi';
import { IconInfo } from '../Icons';

const DASH = '—';

function fmtPct(value, { signed = true } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return DASH;
  const num = Number(value);
  const sign = signed && num > 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
}

function fmtCurrency(value, { signed = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return DASH;
  const num = Number(value);
  const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (signed) return `${num < 0 ? '-' : '+'}$${abs}`;
  return `${num < 0 ? '-' : ''}$${abs}`;
}

function sentiment(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
  const num = Number(value);
  if (num > 0) return 'vd-positive';
  if (num < 0) return 'vd-negative';
  return '';
}

function InfoTip({ text }) {
  return (
    <span className="vd-tile-info" title={text} aria-label={text}>
      <IconInfo size={12} color="currentColor" />
    </span>
  );
}

/** Thin proportional bar; segments with no magnitude collapse to a neutral track. */
function SegmentBar({ segments }) {
  const usable = (segments || []).filter((s) => Number.isFinite(s.value) && Math.abs(s.value) > 0);
  const total = usable.reduce((sum, s) => sum + Math.abs(s.value), 0);
  if (!usable.length || total <= 0) {
    return <div className="vd-tile-bar vd-tile-bar-empty" aria-hidden="true" />;
  }
  return (
    <div className="vd-tile-bar" aria-hidden="true">
      {usable.map((s, i) => (
        <span
          key={`${s.tone}-${i}`}
          className={`vd-tile-bar-seg ${s.tone}`}
          style={{ width: `${(Math.abs(s.value) / total) * 100}%` }}
        />
      ))}
    </div>
  );
}

function Tile({ title, headline, headlineTone = '', segments, rows }) {
  return (
    <div className="vd-tile">
      <span className="vd-tile-title">{title}</span>
      <div className={`vd-tile-headline ${headlineTone}`}>{headline}</div>
      <SegmentBar segments={segments} />
      <div className="vd-tile-rows">
        {rows.map((row) => (
          <div className="vd-tile-row" key={row.label}>
            <span className="vd-tile-row-label">
              <span className={`vd-tile-dot ${row.dot || ''}`} />
              {row.label}
              {row.tip && <InfoTip text={row.tip} />}
            </span>
            <span className={`vd-tile-row-value ${row.tone || ''}`}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY = {
  gain: { total: null, monthly: null, yearly: null, drawdown: null },
  equity: { total: null, investment: null, holdings: null, cash: null },
  profit: { total: null, realized: null, unrealized: null, dividends: null },
  cash: { deposits: null, withdrawals: null, commissions: null },
  broker: null,
};

function isNinjaFutures(summary, dataSeriesName) {
  if (summary?.broker === 'NINJA') return true;
  return String(dataSeriesName || '').split('|')[0] === 'NINJA';
}

export default function AccountSmartTiles({ dataSeriesName, dateRange = 'All' }) {
  const [summary, setSummary] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!dataSeriesName) {
      setSummary(EMPTY);
      return undefined;
    }
    setLoading(true);
    fetchClientSummary(dataSeriesName, dateRange)
      .then((res) => {
        if (!cancelled) setSummary(res.summary || EMPTY);
      })
      .catch((err) => {
        console.error('[smart-tiles]', err);
        if (!cancelled) setSummary(EMPTY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [dataSeriesName, dateRange]);

  const { gain, equity, profit, cash } = summary;
  const ninjaFutures = isNinjaFutures(summary, dataSeriesName);

  return (
    <div className={`vd-tiles${loading ? ' vd-tiles-loading' : ''}`}>
      <Tile
        title="Gain"
        headline={fmtPct(gain.total)}
        headlineTone={sentiment(gain.total)}
        segments={[{ value: gain.total, tone: gain.total >= 0 ? 'vd-seg-pos' : 'vd-seg-neg' }]}
        rows={[
          { label: 'Monthly', value: fmtPct(gain.monthly), tone: sentiment(gain.monthly), dot: 'vd-dot-a', tip: 'Time-weighted return over the trailing 30 days.' },
          { label: 'Yearly', value: fmtPct(gain.yearly), tone: sentiment(gain.yearly), dot: 'vd-dot-b', tip: 'Time-weighted return over the trailing 365 days.' },
          { label: 'Drawdown', value: fmtPct(gain.drawdown, { signed: false }), tone: 'vd-negative', dot: 'vd-dot-neg', tip: ninjaFutures ? 'Largest peak-to-trough decline of net liquidity over the selected range.' : 'Largest peak-to-trough decline of the return index over the selected range.' },
        ]}
      />

      <Tile
        title={ninjaFutures ? 'Net liquidity' : 'Equity'}
        headline={fmtCurrency(equity.total)}
        segments={[
          { value: equity.investment, tone: 'vd-seg-a' },
          { value: equity.total != null && equity.investment != null ? equity.total - equity.investment : null, tone: 'vd-seg-b' },
        ]}
        rows={[
          { label: ninjaFutures ? 'Starting balance' : 'Investment', value: fmtCurrency(equity.investment), dot: 'vd-dot-a', tip: ninjaFutures ? 'Net deposits minus withdrawals.' : 'Net contributed capital: total deposits minus withdrawals.' },
          { label: ninjaFutures ? 'Open P/L' : 'Holdings', value: fmtCurrency(equity.holdings, { signed: true }), tone: sentiment(equity.holdings), dot: 'vd-dot-b', tip: ninjaFutures ? 'Unrealized P/L on open positions from Tradovate.' : 'Market value of open positions from the last broker sync.' },
          { label: ninjaFutures ? 'Net liquidity' : 'Cash', value: fmtCurrency(equity.cash), dot: 'vd-dot-c', tip: ninjaFutures ? 'Current Tradovate net liquidity for this account.' : 'Uninvested cash balance. Coming soon.' },
        ]}
      />

      <Tile
        title="Profit"
        headline={fmtCurrency(profit.total, { signed: true })}
        headlineTone={sentiment(profit.total)}
        segments={[
          { value: profit.realized, tone: profit.realized >= 0 ? 'vd-seg-pos' : 'vd-seg-neg' },
          { value: profit.unrealized, tone: 'vd-seg-a' },
        ]}
        rows={[
          { label: 'Realized', value: fmtCurrency(profit.realized, { signed: true }), tone: sentiment(profit.realized), dot: 'vd-dot-pos', tip: 'Booked profit and loss from closed trades.' },
          {
            label: 'Unrealized',
            // When cost basis is known this is measured, not derived, so show
            // the percentage too — it is the figure brokers headline (Coinbase
            // shows exactly this on its home screen).
            value: profit.unrealizedPct != null
              ? `${fmtCurrency(profit.unrealized, { signed: true })} · ${profit.unrealizedPct >= 0 ? '+' : ''}${profit.unrealizedPct.toFixed(2)}%`
              : fmtCurrency(profit.unrealized, { signed: true }),
            tone: sentiment(profit.unrealized),
            dot: 'vd-dot-a',
            tip: profit.unrealizedPct != null
              ? `Mark-to-market on assets still held, against a cost basis of ${fmtCurrency(profit.holdingsBasis)}. Excludes anything already sold or withdrawn — which is why it differs from Gain.`
              : 'Total profit minus realized: the mark-to-market on what is still held.',
          },
          { label: 'Dividends', value: fmtCurrency(profit.dividends, { signed: true }), tone: sentiment(profit.dividends), dot: 'vd-dot-b', tip: 'Dividend income (IBKR only for now).' },
        ]}
      />

      <Tile
        title="Cash"
        headline={fmtCurrency(
          cash.deposits != null || cash.withdrawals != null
            ? (Number(cash.deposits) || 0) - (Number(cash.withdrawals) || 0)
            : null,
          { signed: true },
        )}
        segments={[
          { value: cash.deposits, tone: 'vd-seg-pos' },
          { value: cash.withdrawals, tone: 'vd-seg-neg' },
        ]}
        rows={[
          { label: 'Deposits', value: fmtCurrency(cash.deposits), tone: 'vd-positive', dot: 'vd-dot-pos', tip: 'Total external cash deposited into the account.' },
          { label: 'Withdrawals', value: fmtCurrency(cash.withdrawals), tone: 'vd-negative', dot: 'vd-dot-neg', tip: 'Total external cash withdrawn from the account.' },
          { label: 'Commissions', value: fmtCurrency(cash.commissions), dot: 'vd-dot-c', tip: 'Fees and commissions paid.' },
        ]}
      />
    </div>
  );
}
